import { useEffect, useRef, useState } from 'react';
import { getSyncStatus, subscribeSync } from '../../api/storage';
import { usePhone } from '../../hooks/usePhone';
import { COLUMN_OPTIONS } from '../../constants/options';
import { useApp } from '../../context/AppContext';
import { useBilling } from '../../context/BillingContext';
import { useI18n } from '../../i18n/useI18n';
import { useUi } from '../../context/UiContext';
import { useClock } from '../../hooks/useClock';
import {
  IconChevronsRight,
  IconClock,
  IconEye,
  IconEyeOff,
  IconHelp,
  IconMaximize,
  IconRepeat,
  IconUser,
} from '../icons/Icons';
import { AccountMenu } from '../overlays/AccountMenu';
import styles from './StatusBar.module.css';

export function StatusBar() {
  const { state, actions } = useApp();
  const { lang, t } = useI18n();
  const { ui, toggle } = useUi();
  const billing = useBilling();
  const phone = usePhone();
  const accountRef = useRef(null);
  const now = useClock();
  const time = now.toLocaleTimeString(lang === 'ar' ? 'ar' : 'en-US', {
    hour: 'numeric',
    minute: '2-digit',
    numberingSystem: 'latn',
  });
  const away = state.dayOffset !== 0;
  const [sync, setSync] = useState(getSyncStatus);
  useEffect(() => subscribeSync(setSync), []);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) document.documentElement.requestFullscreen?.();
    else document.exitFullscreen?.();
  };

  return (
    <footer className={styles.bar}>
      <div className={styles.left}>
        {!state.preferences.sidebarOpen && (
          <button
            type="button"
            className={styles.iconBtn}
            aria-label={t('prefs.show')}
            data-mirror=""
            onClick={() => actions.setPref('sidebarOpen', true)}
          >
            <IconChevronsRight />
          </button>
        )}
        <button
          type="button"
          className={styles.iconBtn}
          aria-pressed={state.preferences.showCompleted}
          aria-label={state.preferences.showCompleted ? t('prefs.hideDone') : t('prefs.showDone')}
          onClick={() => actions.togglePref('showCompleted')}
        >
          {state.preferences.showCompleted ? <IconEye /> : <IconEyeOff />}
        </button>
        {sync === 'offline' || sync === 'error' ? (
          <span className={styles.offline}>{sync === 'offline' ? t('sync.offline') : t('sync.error')}</span>
        ) : null}
        <button
          type="button"
          className={ui.recurringOpen ? `${styles.iconBtn} ${styles.iconOn}` : styles.iconBtn}
          aria-label={t('recur.title')}
          aria-pressed={ui.recurringOpen}
          onClick={() => toggle('recurringOpen')}
        >
          <IconRepeat />
        </button>
      </div>

      <div className={styles.center}>
        {!phone && (
        <div className={styles.cols} role="radiogroup" aria-label={t('status.days')}>
          {COLUMN_OPTIONS.map((count) => {
            const selected = state.preferences.columns === count;
            return (
              <button
                key={count}
                type="button"
                role="radio"
                aria-checked={selected}
                className={selected ? `${styles.col} ${styles.colOn}` : styles.col}
                onClick={() => actions.setPref('columns', count)}
              >
                {count}
              </button>
            );
          })}
        </div>
        )}
        <button
          type="button"
          className={away ? `${styles.clock} ${styles.away}` : styles.clock}
          onClick={() => actions.setOffset(0)}
          title={t('status.today')}
        >
          <IconClock />
          <span>{time}</span>
        </button>
      </div>

      <div className={styles.right}>
        {(billing?.pro || billing?.showPlans !== false) && (
          <button
            type="button"
            className={styles.pro}
            onMouseDown={(event) => event.stopPropagation()}
            onClick={() => toggle('proOpen')}
          >
            {billing?.pro ? t('status.proActive') : t('status.pro')}
          </button>
        )}
        <div className={styles.anchor} ref={accountRef}>
          <button
            type="button"
            className={styles.iconBtn}
            aria-label={t('status.account')}
            aria-expanded={ui.accountOpen}
            onClick={() => toggle('accountOpen')}
          >
            <IconUser />
          </button>
          {ui.accountOpen && <AccountMenu anchorRef={accountRef} />}
        </div>
        {!phone && (
          <button type="button" className={styles.iconBtn} aria-label={t('status.full')} onClick={toggleFullscreen}>
            <IconMaximize />
          </button>
        )}
        {!phone && (
        <button
          type="button"
          className={styles.iconBtn}
          aria-label={t('status.shortcuts')}
          aria-expanded={ui.helpOpen}
          onMouseDown={(event) => event.stopPropagation()}
          onClick={() => toggle('helpOpen')}
        >
          <IconHelp />
        </button>
        )}
      </div>
    </footer>
  );
}
